// /catalog for a dealer: what it can quote today, grouped by category name. A
// Dealer sees list price and floor; a Reseller sees its buy price and its price.
// Only quotable products are listed; nothing here explains Phoneware's catalog work.
import React, { useState } from "react";
import { formatCents, type PricedProduct } from "@phoneware/lens-domain";
import { BILLING, plural } from "../../format.js";
import { useLoad, useSession } from "../../session.js";
import { Callout, Head } from "../../ui.js";
import { categoriesOf } from "./model.js";

export function DealerCatalog(): React.ReactElement {
  const { me, api } = useSession();
  const wholesale = me.dealer?.model === "wholesale";
  const catalog = useLoad<PricedProduct[]>(() => api.pricedCatalog(), []);
  const [category, setCategory] = useState("");
  const list = (catalog.data ?? []).filter((p) => p.quotable);
  const categories = categoriesOf(list);
  const shown = category ? categories.filter((c) => c === category) : categories;

  return (
    <div className="omni-cat" data-testid="dealer-catalog">
      <Head
        title="Catalog"
        sub={
          wholesale
            ? "Your buy price from Phoneware and the price your quotes start from."
            : "Phoneware\u2019s list prices. Discount down to the floor; below it, Phoneware approves the quote first."
        }
      />
      {catalog.error ? (
        <Callout kind="stop" icon="triangle-alert">
          {catalog.error.message}
        </Callout>
      ) : !catalog.data ? (
        <section className="card">
          <div className="empty">Loading the catalog.</div>
        </section>
      ) : list.length === 0 ? (
        <section className="card">
          <div className="empty" data-testid="dealer-catalog-empty">
            Phoneware hasn{"\u2019"}t switched on any products for dealers yet.
          </div>
        </section>
      ) : (
        <>
          {categories.length > 1 ? (
            <div className="bar">
              <select className="input slim cat-sel" aria-label="Category" data-testid="dealer-cat-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="stack">
            {shown.map((cat) => {
              const items = list.filter((p) => p.category === cat);
              return (
                <section key={cat} className="card" data-testid={`dealer-cat-${cat}`}>
                  <div className="card-h">
                    <h2>{cat}</h2>
                    <span className="note">{plural(items.length, "product")}</span>
                  </div>
                  <div className="tablewrap">
                    <table className={`t t-cat ${wholesale ? "t-wholesale" : "t-agent"}`}>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="hide-sm">Billed</th>
                          {wholesale ? (
                            <>
                              <th className="num">Your buy</th>
                              <th className="num">Your price</th>
                              <th className="num">Margin</th>
                            </>
                          ) : (
                            <>
                              <th className="num">List price</th>
                              <th className="num">Floor</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((p) => {
                          const margin = p.buyPrice === null ? null : p.listPrice - p.buyPrice;
                          return (
                            <tr key={p.id} data-testid={`dealer-row-${p.id}`}>
                              <td>
                                <span className="pname">{p.name}</span>
                                {p.code ? <span className="code">{p.code}</span> : null}
                              </td>
                              <td className="hide-sm">
                                {BILLING[p.billing]}
                                {p.unit ? <span className="sub">{p.unit}</span> : null}
                              </td>
                              {wholesale ? (
                                <>
                                  <td className="num">{p.buyPrice === null ? <span className="muted">Not set</span> : formatCents(p.buyPrice)}</td>
                                  <td className="num">{formatCents(p.listPrice)}</td>
                                  <td className="num">
                                    {margin === null ? (
                                      <span className="muted">None</span>
                                    ) : (
                                      <span className={margin < 0 ? "g-bad" : undefined}>
                                        {formatCents(margin)} <span className="muted mg-pct">{p.listPrice > 0 ? Math.round((margin / p.listPrice) * 100) : 0}%</span>
                                      </span>
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="num">{formatCents(p.listPrice)}</td>
                                  <td className="num">{p.floorPrice === null ? <span className="muted">None</span> : formatCents(p.floorPrice)}</td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
